import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useNotifStore } from '../stores/notif';

type Notif = {
  id: number;
  tipo: string;
  titulo: string;
  cuerpo: string;
  data_url: string | null;
  leida: boolean;
  creado_en: string;
};

const TIPO_ICON: Record<string, string> = {
  reclamo:    '📋',
  aviso:      '📰',
  oferta:     '🎁',
  emergencia: '🆘',
  infraccion: '⚠️',
  sistema:    '🔔',
};

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)    return 'ahora';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

// ── SwipeItem ─────────────────────────────────────────────────────────────────
// Detecta swipe horizontal vs. scroll vertical desde el primer movimiento.
// Si el gesto es horizontal, bloquea el scroll y aplica translateX.
// Al soltar con |dx| > 80px → elimina el item.
const THRESHOLD = 80;

function SwipeItem({
  n, onTap, onDelete,
}: {
  n: Notif;
  onTap: (n: Notif) => void;
  onDelete: (id: number) => void;
}) {
  const [tx,         setTx]         = useState(0);       // translateX actual
  const [exiting,    setExiting]     = useState(false);   // animación de salida
  const [direction,  setDirection]   = useState<'left'|'right'|null>(null); // dirección del swipe

  const startX   = useRef(0);
  const startY   = useRef(0);
  const axis     = useRef<'h'|'v'|null>(null);  // bloqueado al inicio del gesto
  const dragging = useRef(false);

  function onTouchStart(e: React.TouchEvent) {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    axis.current   = null;
    dragging.current = false;
  }

  function onTouchMove(e: React.TouchEvent) {
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;

    if (!axis.current) {
      // Decidir eje en el primer movimiento significativo
      if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
      axis.current = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    }

    if (axis.current === 'v') return; // deja scrollear

    e.preventDefault(); // bloquear scroll durante swipe horizontal
    dragging.current = true;
    setTx(dx);
    setDirection(dx < 0 ? 'left' : 'right');
  }

  function onTouchEnd() {
    if (!dragging.current || axis.current !== 'h') {
      setTx(0);
      return;
    }
    if (Math.abs(tx) >= THRESHOLD) {
      // Animar hasta fuera de pantalla y borrar
      setExiting(true);
      setTx(tx < 0 ? -400 : 400);
      setTimeout(() => onDelete(n.id), 280);
    } else {
      // Volver al lugar
      setTx(0);
      setDirection(null);
    }
    dragging.current = false;
  }

  if (exiting) {
    // Mantener el espacio mientras colapsa la altura
    return (
      <li
        style={{
          overflow: 'hidden',
          maxHeight: 0,
          transition: 'max-height 0.28s ease',
        }}
      />
    );
  }

  // Color del fondo de "borrar" según dirección
  const deleteBg = direction
    ? `linear-gradient(${direction === 'left' ? 'to left' : 'to right'}, #fee2e2, #fecaca)`
    : 'transparent';

  return (
    <li
      className="relative overflow-hidden border-b border-gray-50"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Fondo rojo que aparece mientras se desliza */}
      <div
        className="absolute inset-0 flex items-center justify-end px-5"
        style={{
          background: deleteBg,
          opacity: Math.min(Math.abs(tx) / THRESHOLD, 1),
          flexDirection: direction === 'left' ? 'row' : 'row-reverse',
        }}
      >
        <span className="text-red-500 font-bold text-sm flex items-center gap-1.5">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Eliminar
        </span>
      </div>

      {/* Contenido deslizable */}
      <div
        className="flex items-start gap-3 px-5 py-3.5 cursor-pointer active:bg-gray-50"
        style={{
          transform: `translateX(${tx}px)`,
          transition: dragging.current ? 'none' : 'transform 0.25s ease',
          backgroundColor: n.leida ? '#ffffff' : '#f0f0fc',
          willChange: 'transform',
        }}
        onClick={() => !dragging.current && onTap(n)}
      >
        <span className="text-2xl leading-none mt-0.5 flex-shrink-0">
          {TIPO_ICON[n.tipo] ?? '🔔'}
        </span>
        <div className="flex-1 min-w-0">
          <p className={`text-sm leading-tight ${n.leida ? 'text-gray-700' : 'text-gray-900 font-semibold'}`}>
            {n.titulo}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 leading-tight line-clamp-2">{n.cuerpo}</p>
        </div>
        <span className="text-xs text-gray-400 flex-shrink-0 mt-0.5">{timeAgo(n.creado_en)}</span>
        {!n.leida && (
          <span className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: '#32329f' }} />
        )}
      </div>
    </li>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────────
type Props = { open: boolean; onClose: () => void };

export default function NotifPanel({ open, onClose }: Props) {
  const nav      = useNavigate();
  const setCount = useNotifStore((s) => s.setCount);

  const [items,   setItems]   = useState<Notif[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api<{ items: Notif[]; no_leidas: number }>('/notificaciones')
      .then((d) => {
        setItems(d.items);
        setCount(d.no_leidas);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, setCount]);

  async function marcarTodas() {
    await api('/notificaciones?action=marcar_todas_leidas', { method: 'POST' }).catch(() => {});
    setItems((prev) => prev.map((n) => ({ ...n, leida: true })));
    setCount(0);
  }

  async function tocarNotif(n: Notif) {
    if (!n.leida) {
      await api('/notificaciones?action=marcar_leida', {
        method: 'POST',
        body: JSON.stringify({ id: n.id }),
      }).catch(() => {});
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, leida: true } : x));
      setCount(Math.max(0, useNotifStore.getState().count - 1));
    }
    if (n.data_url) {
      onClose();
      nav(n.data_url);
    }
  }

  async function eliminarNotif(id: number) {
    // Actualizar UI inmediatamente
    const eliminada = items.find((n) => n.id === id);
    setItems((prev) => prev.filter((n) => n.id !== id));
    if (eliminada && !eliminada.leida) {
      setCount(Math.max(0, useNotifStore.getState().count - 1));
    }
    // Persistir en BD
    await api(`/notificaciones?action=eliminar&id=${id}`, { method: 'DELETE' }).catch(() => {});
  }

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
        onClick={onClose}
      />

      <div
        className="fixed left-0 right-0 z-50 bg-white shadow-2xl"
        style={{
          top: 'calc(env(safe-area-inset-top) + 56px)',
          borderRadius: '0 0 20px 20px',
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <span className="font-bold text-base text-gray-900">Notificaciones</span>
          <div className="flex items-center gap-3">
            {items.some((n) => !n.leida) && (
              <button onClick={marcarTodas} className="text-xs font-semibold" style={{ color: '#32329f' }}>
                Marcar todas leídas
              </button>
            )}
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-full"
              style={{ backgroundColor: '#f0f0f0', color: '#505050', fontSize: 14 }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Hint swipe — solo si hay items */}
        {!loading && items.length > 0 && (
          <p className="text-center text-xs py-1.5" style={{ color: '#9ca3af', backgroundColor: '#fafafa', borderBottom: '1px solid #f3f4f6' }}>
            Deslizá para eliminar
          </p>
        )}

        {/* Lista */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="text-center text-gray-400 text-sm py-10">Cargando…</p>
          ) : items.length === 0 ? (
            <div className="text-center py-12 px-6">
              <span className="text-4xl">🔔</span>
              <p className="mt-3 text-gray-500 text-sm">No tenés notificaciones</p>
            </div>
          ) : (
            <ul>
              {items.map((n) => (
                <SwipeItem
                  key={n.id}
                  n={n}
                  onTap={tocarNotif}
                  onDelete={eliminarNotif}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
